/**
 * Carousel Component
 */

import React, { useState } from 'react';
import { useTheme, createGlobalStyle } from 'styled-components';
import { CarouselProps, LayoutSpec, DeviceTypes } from './carousel.types';
import { StyledCarousel, StyledDotSVG } from './carousel.style';

import Button from '../Button/button';
import { ButtonShapes, ButtonSizes, ButtonStyles, buttonSizeMap, IndividualButtonSizes } from '../Button/button.types';
import BREAKPOINTS, { BreakpointsType } from '../../style/breakpoints';
import Handler from '../../commonTypes/handler.types';

import { default as ReactCarousel } from 'react-multi-carousel';
import { ButtonGroupProps, DotProps, CarouselInternalState, ResponsiveType } from 'react-multi-carousel/lib/types';

// Need to go over this again once the Datepicker and it's css loading changes are complete.
import * as carouselStyle from 'react-multi-carousel/lib/styles.css';

// use createGlobalStyle to include css styling from third party lib
// https://styled-components.com/docs/api#createglobalstyle
const CarouselStyle = createGlobalStyle`
   body {
     ${carouselStyle}
   }
 `;

const retrieveBreakpointValue = (numString: string | undefined, fallback: number): number => {
  if (typeof numString !== 'undefined') {
    const value: number = parseInt(numString, 10);
    if (!isNaN(value)) {
      return value;
    }
  }

  return fallback;
};

// Go through the layout config prop and convert any breakpoint strings to the rightful breakpoint
const convertLayoutStrings = (layoutObj: LayoutSpec, breakpoints: BreakpointsType): ResponsiveType => {
  for (const key in layoutObj) {
    if (Object.prototype.hasOwnProperty.call(layoutObj, key)) {
      if (typeof layoutObj[key].breakpoint.max === 'string') {
        const value: string | number = layoutObj[key].breakpoint.max;
        layoutObj[key].breakpoint.max = retrieveBreakpointValue(breakpoints[value as keyof BreakpointsType], 1024);
      }
      if (typeof layoutObj[key].breakpoint.min === 'string') {
        const value: string | number = layoutObj[key].breakpoint.min;
        layoutObj[key].breakpoint.min = retrieveBreakpointValue(breakpoints[value as keyof BreakpointsType], 0);
      }
    }
  }
  return layoutObj as ResponsiveType;
};

const Carousel = ({
  layout,
  autoPlay = false,
  autoPlaySpeed,
  showDots = false,
  afterChange,
  beforeChange,
  showArrows = true,
  draggable = true,
  mobileSwipeable = true,
  infinite = false,
  removeArrowAtWidth = [],
  arrowsOutside = true,
  partialVisible = false,
  children,
  keyBoardControl = false,
  arrowSize = ButtonSizes.XLARGE,
  centerMode = false,
  refHandler,
  customTransition,
  arrowDelay,
  ssr = true,
  deviceType = DeviceTypes.MOBILE,
  className,
  CustomDotComponent,
}: CarouselProps) => {
  const theme = useTheme();
  // Retrieve the breakpoints from the theme / the base ones
  const { breakpoints = BREAKPOINTS, dark } = theme;

  // Convert the layout config so that we only have number values for the breakpoints
  const convertedLayout: ResponsiveType = convertLayoutStrings(layout, breakpoints);

  const [buttonsDisabled, setButtonsDisabled] = useState(false);

  // Create the arrow button group.
  // Have to build it this way since the default arrows aren't something we'd like
  const ButtonGroup = ({ next, previous, carouselState }: ButtonGroupProps) => {
    const cState: CarouselInternalState = carouselState as CarouselInternalState;
    const { currentSlide, totalItems, slidesToShow } = cState;

    // Create handlers for the left and right buttons
    const leftHandler: Handler = {
      onClick: () => {
        if (previous) {
          previous();
        }
        if (arrowDelay) {
          setButtonsDisabled(true);
          setTimeout(() => setButtonsDisabled(false), arrowDelay);
        }
      },
    };
    const rightHandler: Handler = {
      onClick: () => {
        if (next) {
          next();
        }
        if (arrowDelay) {
          setButtonsDisabled(true);
          setTimeout(() => setButtonsDisabled(false), arrowDelay);
        }
      },
    };

    return (
      <div id="sideButtons">
        {(infinite || currentSlide > 0) && (
          <Button
            size={arrowSize}
            styleName={ButtonStyles.DEFAULT}
            shape={ButtonShapes.HOLLOW_BORDERLESS}
            iconName="carousel-arrow-left"
            className="left-button"
            handlers={leftHandler}
            disabled={buttonsDisabled}
            dataAttr={{ 'data-testid': 'left-button' }}
          />
        )}
        {(infinite || totalItems - slidesToShow > currentSlide) && (
          <Button
            size={arrowSize}
            styleName={ButtonStyles.DEFAULT}
            shape={ButtonShapes.HOLLOW_BORDERLESS}
            iconName="carousel-arrow-right"
            className="right-button"
            handlers={rightHandler}
            disabled={buttonsDisabled}
            dataAttr={{ 'data-testid': 'right-button' }}
          />
        )}
      </div>
    );
  };

  const arrowSizeMap: IndividualButtonSizes = buttonSizeMap[arrowSize];

  const arrowHeight: number = arrowSizeMap.height || 48;

  // Create the custom pagination buttons at the bottom
  const CustomDotDefault = ({ onClick, active }: DotProps) => {
    return (
      <StyledDotSVG
        active={active}
        onClick={() => onClick && onClick()}
        height="8px"
        width="8px"
        viewBox="0 0 8 8"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="4" cy="4" r="4" />
      </StyledDotSVG>
    );
  };

  return (
    <>
      <CarouselStyle />
      <StyledCarousel
        arrowsOutside={arrowsOutside}
        removeArrowAtWidth={removeArrowAtWidth}
        layout={convertedLayout}
        arrowHeight={arrowHeight}
        dark={dark}
        className={className}
      >
        <ReactCarousel
          ref={refHandler}
          showDots={showDots}
          autoPlay={autoPlay}
          autoPlaySpeed={autoPlaySpeed}
          afterChange={afterChange}
          beforeChange={beforeChange}
          arrows={false}
          draggable={draggable}
          swipeable={mobileSwipeable}
          infinite={infinite}
          customDot={CustomDotComponent || <CustomDotDefault />}
          customTransition={customTransition}
          ssr={ssr}
          renderButtonGroupOutside={true}
          renderDotsOutside={true}
          customButtonGroup={(showArrows && <ButtonGroup />) || null}
          responsive={convertedLayout}
          partialVisible={!centerMode && partialVisible}
          keyBoardControl={keyBoardControl}
          centerMode={centerMode}
          deviceType={deviceType}
        >
          {children}
        </ReactCarousel>
      </StyledCarousel>
    </>
  );
};

export default Carousel;
